import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import {
    apiListMockups,
    apiGetMockup,
    apiUpsertMockup,
    apiDeleteMockup,
} from 'services/MockupService'

const normalizeRevisionValue = (mockup) => {
    const rawRevision =
        mockup?.viewedRevision ??
        mockup?.revision ??
        mockup?.metadata?.revision ??
        mockup?.metadata?.revisionNumber ??
        1
    const parsedRevision = Number(rawRevision)
    return Number.isFinite(parsedRevision) && parsedRevision > 0
        ? Math.floor(parsedRevision)
        : 1
}

const normalizeLatestRevisionValue = (mockup) => {
    const rawRevision =
        mockup?.latestRevision ??
        mockup?.metadata?.revision ??
        mockup?.metadata?.revisionNumber ??
        mockup?.revision ??
        1
    const parsedRevision = Number(rawRevision)
    return Number.isFinite(parsedRevision) && parsedRevision > 0
        ? Math.floor(parsedRevision)
        : 1
}

const normalizeRevisionSummaries = (mockup) => {
    if (!Array.isArray(mockup?.revisions)) {
        return []
    }

    return mockup.revisions
        .map((revision) => {
            const revisionNumber = Number(revision?.revision)
            if (!Number.isFinite(revisionNumber) || revisionNumber <= 0) {
                return null
            }

            return {
                revision: Math.floor(revisionNumber),
                title:
                    typeof revision?.title === 'string' && revision.title.trim()
                        ? revision.title.trim()
                        : 'Untitled KB Mockup',
                createdAt: revision?.createdAt || revision?.generatedAt || null,
                generatedAt: revision?.generatedAt || revision?.createdAt || null,
                basedOnRevision:
                    Number.isFinite(Number(revision?.basedOnRevision)) && Number(revision?.basedOnRevision) > 0
                        ? Math.floor(Number(revision.basedOnRevision))
                        : null,
                summary:
                    typeof revision?.summary === 'string' ? revision.summary : '',
                prompt:
                    typeof revision?.prompt === 'string' ? revision.prompt : '',
                preset:
                    typeof revision?.preset === 'string' ? revision.preset : '',
                warnings: Array.isArray(revision?.warnings)
                    ? revision.warnings.filter(Boolean)
                    : [],
            }
        })
        .filter(Boolean)
        .sort((left, right) => left.revision - right.revision)
}

const normalizeMockup = (mockup) => {
    if (!mockup || typeof mockup !== 'object') {
        return null
    }

    const now = new Date().toISOString()
    const id =
        typeof mockup.id === 'string' || typeof mockup.id === 'number'
            ? mockup.id.toString()
            : null

    if (!id) {
        return null
    }

    return {
        id,
        title:
            typeof mockup.title === 'string' && mockup.title.trim()
                ? mockup.title.trim()
                : 'Untitled KB Mockup',
        draftHtml: typeof mockup.draftHtml === 'string' ? mockup.draftHtml : '',
        sourceType:
            typeof mockup.sourceType === 'string' && mockup.sourceType.trim()
                ? mockup.sourceType.trim()
                : null,
        sourceId:
            typeof mockup.sourceId === 'string' ||
                typeof mockup.sourceId === 'number'
                ? mockup.sourceId.toString()
                : null,
        metadata:
            mockup.metadata && typeof mockup.metadata === 'object'
                ? mockup.metadata
                : null,
        status:
            typeof mockup.status === 'string' && mockup.status.trim()
                ? mockup.status.trim().toLowerCase()
                : null,
        source:
            mockup.source && typeof mockup.source === 'object'
                ? mockup.source
                : null,
        origin:
            mockup.origin && typeof mockup.origin === 'object'
                ? mockup.origin
                : null,
        publishTarget:
            mockup.publishTarget && typeof mockup.publishTarget === 'object'
                ? mockup.publishTarget
                : null,
        revision: normalizeRevisionValue(mockup),
        viewedRevision: normalizeRevisionValue(mockup),
        latestRevision: normalizeLatestRevisionValue(mockup),
        revisionCount: Number(mockup?.revisionCount || normalizeRevisionSummaries(mockup).length || 0),
        revisions: normalizeRevisionSummaries(mockup),
        availableActions: Array.isArray(mockup.availableActions)
            ? mockup.availableActions
            : [],
        createdAt: mockup.createdAt || now,
        updatedAt: mockup.updatedAt || now,
        latestUpdatedAt: mockup.latestUpdatedAt || mockup.updatedAt || now,
    }
}

const putMockup = (state, mockup, makeActive = false) => {
    const normalized = normalizeMockup(mockup)
    if (!normalized) {
        return
    }

    const id = normalized.id
    const existing = state.items[id]
    state.items[id] = {
        ...(existing || {}),
        ...normalized,
    }
    state.order = [id, ...state.order.filter((x) => x !== id)]

    if (makeActive || !state.currentMockupId) {
        state.currentMockupId = id
    }
}

export const fetchMockups = createAsyncThunk(
    'mockups/fetchMockups',
    async (params = {}) => {
        const response = await apiListMockups(params)
        return response.data
    }
)

export const fetchMockupById = createAsyncThunk(
    'mockups/fetchMockupById',
    async (params) => {
        const response = await apiGetMockup(params)
        return response.data
    }
)

export const saveMockup = createAsyncThunk(
    'mockups/saveMockup',
    async (params) => {
        const response = await apiUpsertMockup(params)
        return response.data
    }
)

export const deleteMockup = createAsyncThunk(
    'mockups/deleteMockup',
    async (params) => {
        const response = await apiDeleteMockup(params)
        return response.data
    }
)

const initialState = {
    items: {},
    order: [],
    currentMockupId: null,
    loading: false,
}

const mockupsSlice = createSlice({
    name: 'mockups',
    initialState,
    reducers: {
        startNewMockup: (state, action) => {
            const payload = action.payload || {}
            const providedId = payload.id
            const id =
                typeof providedId === 'string' || typeof providedId === 'number'
                    ? providedId.toString()
                    : `kb-${nanoid()}`
            const now = new Date().toISOString()

            putMockup(
                state,
                {
                    id,
                    title:
                        typeof payload.title === 'string' && payload.title.trim()
                            ? payload.title.trim()
                            : 'Untitled KB Mockup',
                    draftHtml:
                        typeof payload.initialDraft === 'string'
                            ? payload.initialDraft
                            : '',
                    sourceType: payload.sourceType || null,
                    sourceId:
                        typeof payload.sourceId === 'string' ||
                            typeof payload.sourceId === 'number'
                            ? payload.sourceId.toString()
                            : null,
                    metadata:
                        payload.metadata && typeof payload.metadata === 'object'
                            ? payload.metadata
                            : null,
                    revision: normalizeRevisionValue(payload),
                    createdAt: payload.createdAt || now,
                    updatedAt: now,
                },
                true
            )
        },
        selectMockup: (state, action) => {
            const id = action.payload
            if (!id) {
                state.currentMockupId = null
                return
            }

            const normalizedId = id.toString()
            if (state.items[normalizedId]) {
                state.currentMockupId = normalizedId
                state.order = [
                    normalizedId,
                    ...state.order.filter((x) => x !== normalizedId),
                ]
            }
        },
        updateMockupDraft: (state, action) => {
            const { id, draft, title } = action.payload || {}
            if (!id) {
                return
            }

            const normalizedId = id.toString()
            if (!state.items[normalizedId]) {
                return
            }

            state.items[normalizedId].draftHtml =
                typeof draft === 'string' ? draft : ''
            state.items[normalizedId].title =
                typeof title === 'string' && title.trim()
                    ? title.trim()
                    : state.items[normalizedId].title || 'Untitled KB Mockup'
            state.items[normalizedId].updatedAt = new Date().toISOString()
            state.order = [
                normalizedId,
                ...state.order.filter((x) => x !== normalizedId),
            ]
        },
        renameMockup: (state, action) => {
            const { id, title } = action.payload || {}
            if (!id || typeof title !== 'string') {
                return
            }

            const normalizedId = id.toString()
            if (!state.items[normalizedId]) {
                return
            }

            state.items[normalizedId].title = title.trim() || 'Untitled KB Mockup'
            state.items[normalizedId].updatedAt = new Date().toISOString()
        },
        clearMockups: () => initialState,
    },
    extraReducers: {
        [fetchMockups.pending]: (state) => {
            state.loading = true
        },
        [fetchMockups.fulfilled]: (state, action) => {
            state.loading = false
            const mockups = action.payload?.data?.mockups
            const normalized = Array.isArray(mockups)
                ? mockups.map((item) => normalizeMockup(item)).filter(Boolean)
                : []

            state.items = {}
            state.order = []

            normalized.forEach((mockup) => {
                state.items[mockup.id] = mockup
                state.order.push(mockup.id)
            })

            if (!state.currentMockupId || !state.items[state.currentMockupId]) {
                state.currentMockupId = state.order[0] || null
            }
        },
        [fetchMockups.rejected]: (state) => {
            state.loading = false
        },
        [fetchMockupById.fulfilled]: (state, action) => {
            const mockup = action.payload?.data?.mockup
            putMockup(state, mockup, false)
        },
        [saveMockup.fulfilled]: (state, action) => {
            const mockup = action.payload?.data?.mockup
            putMockup(state, mockup, true)
        },
        [deleteMockup.fulfilled]: (state, action) => {
            const id = action.payload?.data?.id
            const normalizedId =
                typeof id === 'string' || typeof id === 'number'
                    ? id.toString()
                    : null
            if (!normalizedId) {
                return
            }

            delete state.items[normalizedId]
            state.order = state.order.filter((x) => x !== normalizedId)
            if (state.currentMockupId === normalizedId) {
                state.currentMockupId = state.order[0] || null
            }
        },
    },
})

export const {
    startNewMockup,
    selectMockup,
    updateMockupDraft,
    renameMockup,
    clearMockups,
} = mockupsSlice.actions

export default mockupsSlice.reducer
